import React, { useState, useEffect, useRef } from "react";
import { Card, Text, Group, Badge, Button, Stack, Paper, Loader, Center } from "@mantine/core";
import { IconSignature, IconCheck, IconCalendar, IconBuilding } from "@tabler/icons-react";
import { useFetch } from "../../helpers/useFetch.js";
import DocumentSign from "../boarding/DocumentSign.jsx";
import DocumentViewer from "../boarding/DocumentViewer.jsx";
import { optimize } from "svgo";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";

export default function InfectiousnessBlocks({ blocks: propBlocks }) {
  const [blocks, setBlocks] = useState(propBlocks || []);
  const [loading, setLoading] = useState(!propBlocks);
  const [error, setError] = useState(null);

  const [signOpened, setSignOpened] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewerOpened, setViewerOpened] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);

  useEffect(() => {
    // If blocks are passed as props, use them
    if (propBlocks) {
      setBlocks(propBlocks);
      setLoading(false);
      return;
    }

    // Otherwise fetch from API
    const fetchBlocks = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await useFetch("user/contracts/infectiousness");

        if (response.data) {
          setBlocks(response.data);
        } else {
          setBlocks([]);
        }
      } catch (err) {
        console.error("Error fetching infectiousness blocks:", err);
        setError("Nastala chyba při načítání dat");
        setBlocks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlocks();
  }, [propBlocks]);

  const signatureRef = useRef();

  const handleDocumentClick = (document) => {
    if (document.isSigned && document.documentPath) {
      setViewingDocument(document.documentPath);
      setViewerOpened(true);
      return;
    }
    setSelectedDocument(document);
    setSignOpened(true);
  };

  const signDocument = async (fields) => {
    const svgElement = document.createElement("svg");
    const trimElement = signatureRef.current;

    const bBox = trimElement.getBBox();
    svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svgElement.setAttribute("viewBox", `${bBox.x} ${bBox.y} ${bBox.width} ${bBox.height}`);
    svgElement.setAttribute("width", trimElement.getBBox().width);
    svgElement.setAttribute("height", trimElement.getBBox().height);
    svgElement.appendChild(trimElement.cloneNode(true));

    const result = optimize(svgElement.outerHTML, { multipass: true, floatPrecision: 2 });

    let parser = new DOMParser();
    let doc = parser.parseFromString(result.data, "image/svg+xml");
    let optimizedSvg = doc.querySelector("svg");
    optimizedSvg.setAttribute(
      "viewBox",
      `${Math.round(bBox.x)} ${Math.round(bBox.y)} ${Math.round(bBox.width)} ${Math.round(bBox.height)}`
    );
    optimizedSvg.setAttribute("width", Math.round(bBox.width));
    optimizedSvg.setAttribute("height", Math.round(bBox.height));

    const serialized = new XMLSerializer().serializeToString(optimizedSvg);

    const payload = btoa(serialized);

    useFetch(`/user/contracts/infectiousness`, "POST", {
      type: selectedDocument.type,
      status: "signed",
      area: selectedDocument.area.id,
      sessions: selectedDocument.sessions.map((session) => session.id).join(","),
      fields:
        fields &&
        Object.keys(fields).map((field) => {
          let value = fields[field];
          if (dayjs(value).isValid()) {
            value = dayjs(value).format("D. M. YYYY");
          }
          return {
            variable: field,
            value: value,
          };
        }),
      svg: payload,
    })
      .then(async (res) => {
        if (res.status === "ok") {
          toast.success("Dokument úspěšně podepsán.");
          // Refetch blocks after successful signature
          try {
            const response = await useFetch("user/contracts/infectiousness");
            if (response.data) {
              setBlocks(response.data);
            }
          } catch (err) {
            console.error("Error refetching blocks:", err);
          }
        } else {
          toast.error("Nastala chyba při podpisu dokumentu a byla nahlášena.");
        }
        setSignOpened(false);
      })
      .catch((err) => {
        toast.error("Nastala chyba při podpisu dokumentu a byla nahlášena.");
        setSignOpened(false);
        console.error(err);
      });
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="md" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py="xl">
        <Text c="red" size="sm">
          {error}
        </Text>
      </Center>
    );
  }

  if (!blocks || blocks.length === 0) {
    return null;
  }

  // Group blocks by area
  const blocksByArea = blocks.reduce((acc, block) => {
    if (!acc[block.area.id]) {
      acc[block.area.id] = {
        area: block.area,
        blocks: [],
      };
    }
    acc[block.area.id].blocks.push(block);
    return acc;
  }, {});

  return (
    <>
      <Stack gap="md">
        <div>
          <Text size="lg" fw={700} c="gray.9" mb={4}>
            Prohlášení o bezinfekčnosti
          </Text>
          <Text size="sm" c="gray.6">
            Pro každý souvislý blok turnusů je potřeba podepsat prohlášení o bezinfekčnosti.
          </Text>
        </div>

        {Object.values(blocksByArea).map(({ area, blocks: areaBlocks }) => (
          <Card key={area.id} withBorder>
            <Card.Section p="md" pb={8}>
              <Group gap="xs">
                <IconBuilding size={16} color="var(--mantine-color-blue-5)" />
                <Text fw={500} size="sm">
                  {area.name}
                </Text>
              </Group>
            </Card.Section>

            <Stack gap="xs" p="md" pt={0}>
              {areaBlocks.map((block) => (
                <Paper key={block.id} p="md" withBorder>
                  <Group justify="space-between" align="center">
                    <Group gap="md">
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: block.isSigned
                            ? "var(--mantine-color-green-1)"
                            : "var(--mantine-color-yellow-1)",
                          color: block.isSigned ? "var(--mantine-color-green-6)" : "var(--mantine-color-yellow-6)",
                        }}>
                        {block.isSigned ? <IconCheck size={16} /> : <IconCalendar size={16} />}
                      </div>

                      <div>
                        <Text size="sm" fw={500} c="gray.9">
                          {block.startDate} - {block.endDate}
                        </Text>
                        <Text size="xs" c="gray.5">
                          {block.sessions.map((s) => s.name).join(", ")}
                        </Text>
                      </div>
                    </Group>

                    <Group gap="xs">
                      {block.isSigned ? (
                        <Badge variant="light" color="green" size="xs">
                          Podepsáno
                        </Badge>
                      ) : (
                        <Badge variant="light" color="gray" size="xs">
                          Čeká
                        </Badge>
                      )}

                      {block.isSigned ? (
                        <Button variant="outline" size="xs" compact onClick={() => handleDocumentClick(block)}>
                          Zobrazit
                        </Button>
                      ) : block.canSignNow ? (
                        <Button
                          variant="filled"
                          size="xs"
                          compact
                          leftSection={<IconSignature size={12} />}
                          onClick={() => handleDocumentClick(block)}>
                          Podepsat
                        </Button>
                      ) : (
                        <Text size="xs" c="orange.4">
                          Podepisování bude možné 2 dny před začátkem bloku.
                        </Text>
                      )}
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Card>
        ))}
      </Stack>
      {signOpened && (
        <DocumentSign
          signDocument={signDocument}
          signatureRef={signatureRef}
          document={selectedDocument}
          setOpened={setSignOpened}
        />
      )}
      {viewerOpened && <DocumentViewer document={viewingDocument} setOpened={setViewerOpened} />}
    </>
  );
}
